/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { Button, Tabs, Tag } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import moment from 'moment';
import { formatTime, formatDate } from '../../../utils/helpers/dateHelpers';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { MedicalHistoryCard, ReportCard } from '../../../components/Card/Cards';
import Header from '../../../components/Header';
import MemberShipModel from '../../../components/Modal/MemberShipModel';
import AnniversaryModel from '../../../components/Modal/AnniversaryModel';
import Sidebar from '../../../components/Sidebar';
import AdvancedTable from '../../../components/AdvancedTable';
import { GetCustomerDashboard, ActionGetMembershipHistory } from '../../../store/Actions/PatientAction';
import { ReportCardData } from '../../../utils/Data';
import PatientCCPaperView from '../../../components/PatientCCPaperView/PatientCCPaperView';
import './PatientProfile.css';
import CasePaperButton from '../../../components/CasePaperButton/CasePaperButton';
import { calculateAge } from '../../../utils/functions';

const PatientProfile = () => {
  const dispatch = useDispatch();
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  const { patientId } = useParams();
  const pageValue = searchParams.get('page');
  const { CustomerDashbord, CcPaperDetail, loader, membershipHistory } = useSelector(state => state?.patient);
  const [modal, SetModal] = useState({ is: false, id: '' });
  const [anniversaryModal, setAnniversaryModal] = useState({ is: false, id: '' });

  // Extract customer data for statistics
  const customerData = CustomerDashbord?.[0]?.customer;
  const bookingsHistory = CustomerDashbord?.[0]?.bookingsHistory;

  // Compute statistics from dashboard data
  const stats = useMemo(() => {
    if (!CustomerDashbord?.[0]) return null;

    const data = CustomerDashbord[0];
    const treatments = bookingsHistory?.treatments || [];
    const services = bookingsHistory?.services || [];
    const solutions = bookingsHistory?.solutions || [];
    const detoxRetreat = bookingsHistory?.detoxRetreat || [];

    // Calculate total booking days
    const totalBookingDays = [...treatments, ...services, ...solutions, ...detoxRetreat]
      .reduce((sum, booking) => sum + (booking.totalDays || 0), 0);

    // Get active membership
    const activeMembership = customerData?.memberShipDetails?.find(m => {
      const endDate = moment(m.endDate);
      return endDate.isAfter(moment());
    });

    // Calculate remaining membership days
    const remainingMembershipDays = customerData?.totalMemberShipDays || 0;

    return {
      totalAppointments: data.appointmentHistory?.length || 0,
      upcomingAppointments: data.upComingAppointment?.length || 0,
      totalTreatments: treatments.length,
      totalServices: services.length + solutions.length + detoxRetreat.length,
      totalConsultations: data.consultation?.length || 0,
      totalBookingDays,
      totalMedications: data.medicals?.[0]?.purchasedMedicines?.length || 0,
      membershipCount: customerData?.memberShipDetails?.length || 0,
      isMember: customerData?.isMemberShip || false,
      remainingMembershipDays,
      activeMembership: activeMembership?.memberShipType || null,
      activeProgramme: activeMembership?.programme || null,
      activeRoomType: activeMembership?.roomTypeId?.name?.en || null,
      activeOccupancyMode: activeMembership?.occupancyModeId?.code || null,
    };
  }, [CustomerDashbord, customerData, bookingsHistory]);

  useEffect(() => {
    if (patientId) {
      dispatch(GetCustomerDashboard({ Query: `/${patientId}`, obj: undefined }));
      dispatch(ActionGetMembershipHistory({ Query: `${patientId}` }));
    }
  }, [patientId, dispatch]);

  // Column definitions for AdvancedTable
  const upcomingAppointmentColumns = [
    { id: 'doctor', name: 'Doctor', selector: row => row.doctor?.name || '-', sortable: true },
    { id: 'startTime', name: 'Start Time', selector: row => row.startTime || '-', cell: row => formatTime(row.startTime), sortable: true },
    { id: 'consultingFor', name: 'Consulting For', selector: row => row.consultingFor || '-' },
    { id: 'date', name: 'Date', selector: row => row.date || '-', sortable: true },
  ];

  const membershipColumns = [
    { id: 'startDate', name: 'Start Date', selector: row => row.startDate ? moment(row.startDate).format('DD-MM-YYYY') : '-', sortable: true },
    { id: 'endDate', name: 'End Date', selector: row => row.endDate ? moment(row.endDate).format('DD-MM-YYYY') : '-', sortable: true },
    { id: 'memberShipType', name: 'Type', selector: row => row.memberShipType || '-' },
    { id: 'memberShipDays', name: 'Days', selector: row => row.memberShipDays || '-' },
    { id: 'roomTypeName', name: 'Room Type Name', selector: row => row.roomTypeId?.name?.en || '-' },
    { id: 'occupancyMode', name: 'Occupancy Mode', selector: row => row.occupancyModeId?.code || '-' },
    { id: 'programme', name: 'Programme', selector: row => row.programme || '-' },
  ];

  const membershipHistoryColumns = [
    {
      id: 'reason',
      name: 'Reason',
      selector: row => row.reason || '-',
      width: 300,
      cell: row => {
        const orderId = row.orderId;
        if (orderId) {
          return (
            <Link
              to={`/order-history/order-history-overview/${orderId}`}
              style={{ color: '#2e37a4', fontWeight: '500' }}
            >
              {row.reason || '-'}
            </Link>
          );
        }
        return row.reason || '-';
      },
    },
    { id: 'createdAt', name: 'Date', selector: row => row.createdAt ? moment(row.createdAt).format('DD-MM-YYYY: HH:mm A') : '-', sortable: true },
    { id: 'transactionType', name: 'Type', selector: row => row.transactionType || '-', sortable: true },
    { id: 'days', name: 'Days', selector: row => row.days || '-', sortable: true },
    { id: 'previousBalance', name: 'Prev Balance', selector: row => row.previousBalance || '-', sortable: true },
    { id: 'newBalance', name: 'New Balance', selector: row => row.newBalance || '-', sortable: true },
  ];

  const medicationColumns = [
    { id: 'medicineName', name: 'Medicine', selector: row => row.medicineName || row.name || '-', sortable: true },
    { id: 'dosage', name: 'Dosage', selector: row => row.dosage || '-' },
    { id: 'frequency', name: 'Frequency', selector: row => row.frequency || '-' },
  ];

  const appointmentHistoryColumns = [
    { id: 'doctor', name: 'Doctor', selector: row => row.doctor?.name || '-', sortable: true },
    { id: 'date', name: 'Date', selector: row => row.date || '-', sortable: true },
    { id: 'consultingFor', name: 'Consulting For', selector: row => row.consultingFor || '-' },
  ];

  const bookingHistory = CustomerDashbord?.[0]?.bookingsHistory;
  const allBookingsHistory = [
    ...(bookingHistory?.treatments || []),
    ...(bookingHistory?.services || []),
    ...(bookingHistory?.solutions || []),
    ...(bookingHistory?.detoxRetreat || []),
    ...(bookingHistory?.programme || []),
  ];

  const bookingHistoryColumns = [
    {
      id: 'treatment',
      name: 'Treatment/Service/Detox/Programm',
      selector: row => {
        const treatmentName = row.treatmentData?.[0]?.treatmentName;
        const solName = row.solutionName || row.solutionData?.[0]?.solutionName;
        const serviceName = row.serviceName || row.serviceData?.[0]?.name;
        const detoxName = row.detoxRetreatData?.[0]?.title || row.detoxRetreatName || row.detoxRetreatData?.[0]?.detoxRetreatId?.title;
        return treatmentName || solName || serviceName || detoxName || (row.detoxRetreatData?.[0]?.detoxRetreatId?.title) || '-';
      },
      sortable: true,
      width: '300px',
      cell: row => {
        const treatmentName = row.treatmentData?.[0]?.treatmentName;
        const solName = row.solutionName || row.solutionData?.[0]?.solutionName;
        const serviceName = row.serviceName || row.serviceData?.[0]?.name;
        const detoxName = row.detoxRetreatData?.[0]?.title || row.detoxRetreatName || row.detoxRetreatData?.[0]?.detoxRetreatId?.title;
        const programmeName = row.programmeData?.[0]?.name?.en || row.programmeName || row.programmeData?.[0]?.programmeId?.name?.en;
        const displayName = treatmentName || serviceName || solName || detoxName || programmeName || '-';
        // Extract required IDs for the indoor casepaper link
        const currentPatientId = row.patientId?._id || row.customerId?._id || row.customerId;
        const orderId = row.orderId?._id || row.orderId?.id || row.orderId;

        // Find patientPlanId from the array or assessment plan
        const patientPlanId = row.patientPlan?.[0]?._id || row.patientPlan?.[0]?.id || row.assessmentPlan?._id || row.assessmentPlan?.id;

        // If we don't have enough info to link, just show the name
        if (!currentPatientId || !orderId || !patientPlanId) {
          return <span>{displayName + `[${treatmentName ? "Treatment" : serviceName ? "Service" : solName ? "Solution" : detoxName ? "Detox" : "Programme"}]`}</span>;
        }

        // {programmeId: {_id: "65eb0bfa5e006a7d538138ca", name: {en: "5 Year Spondylosis Program"}
        return (
          <Link
            to={`/indoor-casepaper/${currentPatientId}?orderId=${orderId}&patientPlanId=${patientPlanId}`}
            style={{
              color: '#2e37a4',
              fontWeight: '500',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            {displayName + ` [${treatmentName ? "Treatment" : serviceName ? "Service" : solName ? "Solution" : detoxName ? "Detox" : programmeName ? "Programme" : ""}]`}
          </Link>
        );
      },
    },
    {
      id: 'dateRange',
      name: 'Date Range',
      selector: row => {
        if (row.startDate && row.endDate) return `${row.startDate} - ${row.endDate}`;
        return row.date || '-';
      },
      sortable: true,
      width: '200px',
    },
    {
      id: 'totalDays',
      name: 'Days',
      selector: row => row.totalDays || '-',
      sortable: true,
      width: '80px',
      cell: row => (
        <span style={{ fontWeight: '600', color: '#2e37a4' }}>
          {row.totalDays || '-'}
        </span>
      ),
    },
    {
      id: 'status',
      name: 'Status',
      selector: row => row.status || '-',
      sortable: true,
      width: '120px',
      cell: row => {
        const status = row.status;
        let color = 'default';
        if (status === 'Upcoming') color = 'blue';
        else if (status === 'Completed') color = 'green';
        else if (status === 'Cancelled') color = 'red';
        else if (status === 'In Progress') color = 'orange';

        return (
          <Tag color={color} style={{ fontWeight: '500' }}>
            {status || '-'}
          </Tag>
        );
      },
    },
  ];


  // Tab items configuration
  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <div>
          {/* Membership Details */}
          <div className="section-card">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon"><FeatherIcon icon="credit-card" size={16} /></span>
                Membership Details
              </h3>
            </div>
            <div className="section-body">
              <AdvancedTable
                resource="membership"
                columns={membershipColumns}
                data={CustomerDashbord?.[0]?.customer?.memberShipDetails || []}
                loading={loader}
                enableSearch={false}
                serverSide={false}
                defaultPageSize={5}
              />
            </div>
          </div>

          {/* Membership History */}
          <div className="section-card">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon"><FeatherIcon icon="list" size={16} /></span>
                Membership History
              </h3>
            </div>
            <div className="section-body">
              <AdvancedTable
                resource="membershipHistory"
                columns={membershipHistoryColumns}
                data={membershipHistory || []}
                loading={loader}
                enableSearch={false}
                serverSide={false}
                defaultPageSize={5}
              />
            </div>
          </div>

          {/* Upcoming Appointments & Current Medications */}
          <div className="tables-row two-col">
            <div className="section-card">
              <div className="section-header">
                <h3 className="section-title">
                  <span className="section-icon"><FeatherIcon icon="calendar" size={16} /></span>
                  Upcoming Appointments
                </h3>
              </div>
              <div className="section-body">
                <AdvancedTable
                  resource="appointments"
                  columns={upcomingAppointmentColumns}
                  data={CustomerDashbord?.[0]?.upComingAppointment || []}
                  loading={loader}
                  enableSearch={false}
                  serverSide={false}
                  defaultPageSize={5}
                />
              </div>
            </div>

            <div className="section-card">
              <div className="section-header">
                <h3 className="section-title">
                  <span className="section-icon"><FeatherIcon icon="package" size={16} /></span>
                  Current Medications
                </h3>
              </div>
              <div className="section-body">
                <AdvancedTable
                  resource="medications"
                  columns={medicationColumns}
                  data={CustomerDashbord?.[0]?.medicals?.[0]?.purchasedMedicines || []}
                  loading={loader}
                  enableSearch={false}
                  serverSide={false}
                  defaultPageSize={5}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'history',
      label: 'History',
      children: (
        <div>
          {/* Appointment History */}
          <div className="section-card">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon"><FeatherIcon icon="clock" size={16} /></span>
                Appointment History
              </h3>
            </div>
            <div className="section-body">
              <AdvancedTable
                resource="appointmentHistory"
                columns={appointmentHistoryColumns}
                data={CustomerDashbord?.[0]?.appointmentHistory || []}
                loading={loader}
                enableSearch
                serverSide={false}
                defaultPageSize={10}
              />
            </div>
          </div>

          {/* Bookings History */}
          <div className="section-card">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon"><FeatherIcon icon="book-open" size={16} /></span>
                Bookings History
              </h3>
            </div>
            <div className="section-body">
              <AdvancedTable
                resource="bookingHistory"
                columns={bookingHistoryColumns}
                data={allBookingsHistory}
                loading={loader}
                enableSearch
                serverSide={false}
                defaultPageSize={10}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'documents',
      label: 'Documents & Reports',
      children: (
        <div className="tables-row three-col">
          <ReportCard
            Heading="Consultation"
            Data={CustomerDashbord?.[0]?.consultation}
            style={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <MedicalHistoryCard
            Heading="Medical Document"
            style={{ borderRadius: 8, border: '1px solid #e5e7eb', padding: 10 }}
            Data={CustomerDashbord?.[0]?.customer?.laboratoryDocs}
            Children={null}
          />
          <ReportCard Heading="Reports" Data={ReportCardData} />
        </div>
      ),
    },
  ];

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper patient-profile-container">
        <div className="content">
          {/* Breadcrumb */}
          <div className="row">
            <div className="col-sm-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={`/patient-list?page=${pageValue || 1}`}>Patient</Link>
                </li>
                <li className="breadcrumb-item">
                  <FeatherIcon icon="chevron-right" size={14} />
                </li>
                <li className="breadcrumb-item active">Patient Profile</li>
              </ul>
            </div>
          </div>

          {/* Profile Header with all info */}
          <PatientCCPaperView
            userId={patientId}
            groupedInfo={{
              'Patient Details': {
                'Age': CcPaperDetail?.birthDate ? calculateAge(CcPaperDetail.birthDate) : CcPaperDetail?.age,
                'Registration No': customerData?.registrationNo,
                'Old Case No': CcPaperDetail?.oldCasePaperNo,
                'Height': CcPaperDetail?.height,
                'Weight': CcPaperDetail?.weight,
                'Anniversary Date (DD/MM/YYYY)': CcPaperDetail?.anniversaryDate ? formatDate(CcPaperDetail?.anniversaryDate) : '-',
                'Address': customerData?.address || CcPaperDetail?.address,
              },
              'Membership & Statistics': {
                'Membership': stats?.isMember ? 'Active' : null,
                'Membership Type': stats?.activeMembership,
                'Remaining Days': stats?.remainingMembershipDays,
                'Total Treatments': stats?.totalTreatments,
                'Total Appointments': stats?.totalAppointments,
                'Consultations': stats?.totalConsultations,
                'Total Booking Days': stats?.totalBookingDays,
                'Active Room Type': stats?.activeRoomType,
                'Active Occupancy Mode': stats?.activeOccupancyMode,
              },
              'Extra Details': {
                'Emergency Number': CcPaperDetail?.confirmationDetails?.emergencyNumber,
                'Vehicle No': CcPaperDetail?.confirmationDetails?.vehicleNo,
                'Arriving From': CcPaperDetail?.confirmationDetails?.arrivingFrom,
                'Arrival Time': formatTime(CcPaperDetail?.confirmationDetails?.arrivalTime),
                'Aadhar Cards': CcPaperDetail?.confirmationDetails?.aadharCards?.join(', '),
              },
              'Insurance Information': {
                'Insurance Company': CcPaperDetail?.insurance?.insuranceCompany,
                'Policy Number': CcPaperDetail?.insurance?.policyNumber,
                'Policy Holder': CcPaperDetail?.insurance?.policyHolderName,
                'Claim Amount': CcPaperDetail?.insurance?.claimAmount ? `₹${CcPaperDetail.insurance.claimAmount.toLocaleString()}` : null,
                'Insurance Contact': CcPaperDetail?.insurance?.contactNumber,
              },
            }}
          />

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            justifyContent: 'flex-end'
          }}>
            <Link
              to={`/20minform?id=${patientId}&&for=125&&update=false`}
              className="btn"
              style={{
                background: '#2e37a4',
                color: '#fff',
                borderRadius: '8px',
                padding: '10px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                fontWeight: '500',
                transition: 'all 0.2s ease',
              }}
            >
              <FeatherIcon icon="file-text" size={16} />
              View Health Assessment
            </Link>

            {/* CasePaper Button Component with modal and API handling */}
            <CasePaperButton patientId={patientId} />

            <Button
              type="primary"
              style={{
                background: '#10b981',
                borderColor: '#10b981',
                borderRadius: '8px',
                padding: '10px 20px',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
              }}
              onClick={() => SetModal({ is: true, id: patientId })}
            >
              <FeatherIcon icon="award" size={16} />
              {customerData?.memberShipDetails?.length > 0
                ? 'Edit Membership'
                : 'Add Membership'}
            </Button>

            <Button
              type="primary"
              style={{
                background: '#ff7675',
                borderColor: '#ff7675',
                borderRadius: '8px',
                padding: '10px 20px',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
              }}
              onClick={() => setAnniversaryModal({ is: true, id: patientId })}
            >
              <FeatherIcon icon="heart" size={16} />
              Update Anniversary
            </Button>
          </div>

          {/* Tabbed Content */}
          <Tabs
            defaultActiveKey="overview"
            items={tabItems}
            size="large"
            style={{ marginTop: 24 }}
          />
        </div>
      </div>

      <MemberShipModel
        IsOpen={modal}
        Isclose={() => SetModal({ is: false, id: '' })}
        record={customerData}
        MemberVerification={false}
      />

      <AnniversaryModel
        IsOpen={anniversaryModal}
        Isclose={() => setAnniversaryModal({ is: false, id: '' })}
        customerData={customerData}
        confirmationDetails={CcPaperDetail?.confirmationDetails}
      />
    </>
  );
};

export default PatientProfile;
